import { type FC, memo, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";
import { Highlight } from "prism-react-renderer";
import { TooltipIconButton } from "~/components/assistant-v2/TooltipIconButton";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { codeTheme } from "~/lib/codeTheme";

interface HighlightedCodeBlockProps {
  code: string;
  language: string;
}

export const HighlightedCodeBlock: FC<HighlightedCodeBlockProps> = memo(
  function HighlightedCodeBlock({ code, language }) {
    const { Prism } = usePrismLanguages();
    const [isCopied, setIsCopied] = useState(false);
    const handleCopy = () => {
      if (!code || isCopied) {
        return;
      }
      navigator.clipboard.writeText(code).then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
      });
    };

    return (
      <div className="group relative my-4 min-w-0">
        <TooltipIconButton
          tooltip="Copy"
          onClick={handleCopy}
          className="absolute top-2 right-2 opacity-0 transition-opacity group-hover:opacity-100"
        >
          {isCopied ? <CheckIcon /> : <CopyIcon />}
        </TooltipIconButton>
        <Highlight theme={codeTheme} code={code} language={language} prism={Prism}>
          {({ tokens, getLineProps, getTokenProps }) => (
            <pre className="aui-md-pre overflow-x-auto rounded-lg bg-muted p-4 font-mono text-xs">
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              ))}
            </pre>
          )}
        </Highlight>
      </div>
    );
  }
);
